import React, { useState, useEffect } from "react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
} from "@headlessui/react";
import {
  ChevronDownIcon,
  CheckIcon,
  XIcon,
  RefreshCcwIcon,
} from "lucide-react";
import { User } from "../../../../types";
import {
  getUsers,
  getAllUsersIncludingArchived,
  archiveUser,
  restoreUser,
} from "../../../../service/apiService";
import { useUser } from "../../../../context/UserContext";

type ArchiveStep = "idle" | "archiving" | "archived";
type RestoreStep = "idle" | "restoring" | "restored";
type ActionType = "archive" | "restore" | null;

interface ArchiveUserProps {
  onUserChange: () => void;
}

const ArchiveUser: React.FC<ArchiveUserProps> = ({ onUserChange }) => {
  const { userId } = useUser();

  const [activeUsers, setActiveUsers] = useState<User[]>([]);
  const [selectedArchiveUser, setSelectedArchiveUser] = useState<User | null>(null);
  const [archiveQuery, setArchiveQuery] = useState("");
  const [isLoadingActiveUsers, setIsLoadingActiveUsers] = useState(false);
  const [archiveStep, setArchiveStep] = useState<ArchiveStep>("idle");
  const [archiveSuccess, setArchiveSuccess] = useState<boolean | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);

  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [selectedRestoreUser, setSelectedRestoreUser] = useState<User | null>(null);
  const [restoreQuery, setRestoreQuery] = useState("");
  const [isLoadingAllUsers, setIsLoadingAllUsers] = useState(false);
  const [restoreStep, setRestoreStep] = useState<RestoreStep>("idle");
  const [restoreSuccess, setRestoreSuccess] = useState<boolean | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);

  const isArchiving = archiveStep === "archiving";
  const isArchived = archiveStep === "archived";
  const isRestoring = restoreStep === "restoring";
  const isRestored = restoreStep === "restored";

  useEffect(() => {
    const fetchActiveUsers = async () => {
      setIsLoadingActiveUsers(true);
      try {
        const result = await getUsers();
        setActiveUsers(result);
      } catch (error) {
        console.error("Error fetching active users:", error);
      } finally {
        setIsLoadingActiveUsers(false);
      }
    };
    fetchActiveUsers();
  }, []);

  useEffect(() => {
    const fetchAllUsers = async () => {
      setIsLoadingAllUsers(true);
      try {
        const result = await getAllUsersIncludingArchived();
        setAllUsers(result);
      } catch (error) {
        console.error("Error fetching all users including archived:", error);
      } finally {
        setIsLoadingAllUsers(false);
      }
    };
    fetchAllUsers();
  }, []);

  const availableArchiveUsers = activeUsers.filter(
    (user) => user.id !== userId
  );

  const filteredArchiveUsers =
    archiveQuery === ""
      ? availableArchiveUsers
      : availableArchiveUsers.filter((user) =>
          user.id
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(archiveQuery.toLowerCase().replace(/\s+/g, ""))
        );

  const availableRestoreUsers = allUsers.filter(
    (user) =>
      user.id !== userId &&
      (user.user_status === "archived" || user.user_status === "disabled")
  );

  const filteredRestoreUsers =
    restoreQuery === ""
      ? availableRestoreUsers
      : availableRestoreUsers.filter((user) =>
          user.id
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(restoreQuery.toLowerCase().replace(/\s+/g, ""))
        );

  const resetArchiveForm = () => {
    setSelectedArchiveUser(null);
    setArchiveQuery("");
    setArchiveStep("idle");
    setArchiveSuccess(null);
    setArchiveError(null);
    setShowArchiveModal(false);
  };

  const resetRestoreForm = () => {
    setSelectedRestoreUser(null);
    setRestoreQuery("");
    setRestoreStep("idle");
    setRestoreSuccess(null);
    setRestoreError(null);
    setShowRestoreModal(false);
  };

  const handleArchiveClick = () => {
    if (!selectedArchiveUser) return;
    setShowArchiveModal(true);
  };

  const handleRestoreClick = () => {
    if (!selectedRestoreUser) return;
    setShowRestoreModal(true);
  };

  const handleConfirmArchive = async () => {
    setShowArchiveModal(false);
    await handleArchive();
  };

  const handleConfirmRestore = async () => {
    setShowRestoreModal(false);
    await handleRestore();
  };

  const handleArchive = async () => {
    if (!selectedArchiveUser) return;

    setArchiveStep("archiving");
    setArchiveError(null);
    setArchiveSuccess(null);

    try {
      const result = await archiveUser(selectedArchiveUser.id.toLowerCase());

      if (result && !result.error) {
        setArchiveStep("archived");
        setArchiveSuccess(true);
        onUserChange();
        const updated = await getAllUsersIncludingArchived();
        setAllUsers(updated);
      } else {
        setArchiveError(
          result?.error || "Failed to archive user. Please try again."
        );
        setArchiveSuccess(false);
        setArchiveStep("idle");
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;

      if (status === 409) {
        setArchiveError("User is already archived.");
      } else if (status === 404) {
        setArchiveError("User not found. Please check the selected user.");
      } else if (status === 403) {
        setArchiveError("You don't have permission to archive this user.");
      } else if (status === 400) {
        setArchiveError(detail || "You cannot archive your own account.");
      } else {
        setArchiveError(
          detail || err?.message || "Failed to archive user. Please try again."
        );
      }
      setArchiveSuccess(false);
      setArchiveStep("idle");
    }
  };

  const handleRestore = async () => {
    if (!selectedRestoreUser) return;

    setRestoreStep("restoring");
    setRestoreError(null);
    setRestoreSuccess(null);

    try {
      const result = await restoreUser(selectedRestoreUser.id.toLowerCase());

      if (result && !result.error) {
        setRestoreStep("restored");
        setRestoreSuccess(true);
        onUserChange();
        const updated = await getAllUsersIncludingArchived();
        setAllUsers(updated);
        const updatedActive = await getUsers();
        setActiveUsers(updatedActive);
      } else {
        setRestoreError(
          result?.error || "Failed to restore user. Please try again."
        );
        setRestoreSuccess(false);
        setRestoreStep("idle");
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;

      if (status === 409) {
        setRestoreError("User is not archived. Cannot restore.");
      } else if (status === 404) {
        setRestoreError("User not found. Please check the selected user.");
      } else if (status === 403) {
        setRestoreError("You don't have permission to restore this user.");
      } else {
        setRestoreError(
          detail || err?.message || "Failed to restore user. Please try again."
        );
      }
      setRestoreSuccess(false);
      setRestoreStep("idle");
    }
  };

  const renderCombobox = (
    label: string,
    value: User | null,
    onChange: (user: User | null) => void,
    query: string,
    onQueryChange: (q: string) => void,
    filteredUsers: User[],
    availableUsers: User[],
    isLoadingUsers: boolean,
    isDisabled: boolean,
    emptyMessage: string
  ) => (
    <div className="flex flex-col">
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      <Combobox<User>
        value={
          value
            ? availableUsers.find((user) => user.id === value.id)
            : undefined
        }
        onChange={(user: User | null) =>
          user ? onChange(user) : onChange(null)
        }
        disabled={isDisabled}
      >
        <div className="relative mt-1">
          <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-200 focus-visible:ring-offset-2 sm:text-sm h-12">
            <ComboboxInput
              className={`w-full h-full border border-gray-200 py-2 pl-3 pr-10 
                text-sm leading-5 rounded-lg focus:outline-none 
                focus:border-violet-500 focus:ring focus:ring-violet-200 
                focus:ring-opacity-20 ${
                  isDisabled
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700"
                }`}
              displayValue={(user: User) => user?.id ?? ""}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                onQueryChange(event.target.value)
              }/>
            {isLoadingUsers && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-8">
                <RefreshCcwIcon
                  className="animate-spin h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </div>
            )}<ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronDownIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </ComboboxButton>
          </div><ComboboxOptions
            className="absolute z-10 mt-1 max-h-40 w-full overflow-auto
              rounded-md bg-white py-1 text-base shadow-lg ring-1 
              ring-black/5 focus:outline-none sm:text-sm"
          >
            {availableUsers.length === 0 ? (
              <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                {emptyMessage}
              </div>
            ) : filteredUsers.length === 0 && query !== "" ? (
              <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                Nothing found.
              </div>
            ) : (
              filteredUsers.map((user) => (
                <ComboboxOption
                  key={user.id}
                  className={({ active}: { active: boolean }) =>
                    `relative cursor-default select-none py-2 pl-10 pr-4 ${
                      active ? "bg-violet-600 text-white" : "text-gray-900"
                    }`
                  }
                  value={user}
                >
                  {({
                    selected,
                    active,
                  }: {
                    selected: boolean;
                    active: boolean;
                  }) => (
                    <>
                      <span
                        className={`block truncate ${
                          selected ? "font-medium" : "font-normal"
                        }`}
                      >
                        {user.id}
                        {/* Status badge in dropdown */}
                        {user.user_status === "archived" && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600">
                            archived
                          </span>
                        )}
                {user.user_status === "disabled" && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-600">
                            disabled
                          </span>
                        )}
                      </span>
                      {selected ? (
                        <span
                          className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                            active ? "text-white" : "text-violet-600"
                          }`}
                        >
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </div>
      </Combobox>
    </div>
  );

  return (
    <><Disclosure as="div" className="py-2"><DisclosureButton className="group flex w-full items-center justify-between">
          <span className="text-sm font-medium text-gray-900group-hover:text-gray-700">
            Archive / Restore User
          </span>
          <ChevronDownIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-300group-open:rotate-180" />
        </DisclosureButton><DisclosurePanel className="mt-2 text-sm mx-2p-4 rounded-lg bg-white">
          <div className="space-y-8">

            {/* ════════════════════════════════════════════════════════════ARCHIVE SECTION
            ════════════════════════════════════════════════════════════ */}
            <div className="space-y-4">

              {/* Select User Combobox (active users only) */}
              {renderCombobox(
                "Select User to Archive",
                selectedArchiveUser,
                setSelectedArchiveUser,
                archiveQuery,
                setArchiveQuery,
                filteredArchiveUsers,
                availableArchiveUsers,
                isLoadingActiveUsers,
                isArchiving || isArchived,
                "No active users available."
              )}

              {/* Selected user status badge */}
              {selectedArchiveUser && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">Current status:</span>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full
                      text-xs font-medium ${
                        selectedArchiveUser.user_status === "archived"
                          ? "bg-gray-100 text-gray-700"
                          : selectedArchiveUser.user_status === "disabled"
                          ? "bg-red-100 text-red-700"
                          : "bg-green-100 text-green-700"
                      }`}
                  >
                    {selectedArchiveUser.user_status ?? "enabled"}
                  </span>
                </div>
              )}

              {/* Archive success banner */}
              {isArchived && selectedArchiveUser && (
                <div className="rounded-md border border-amber-200 bg-amber-50px-4 py-3">
                  <p className="text-sm font-medium text-amber-800">🗂️ User<strong>{selectedArchiveUser.id}</strong> has been
                    archived successfully.
                  </p><p className="mt-1 text-sm text-amber-700">
                    The user is no longer visible on the UI. Their sessions and
                    messages remain in the database. Their Cognito account has
                    been disabled.
                  </p>
                </div>
              )}

              {/* Archive action row */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                {/* Archive button */}
                {!isArchived && (
                  <button
                    type="button"
                    onClick={handleArchiveClick}
                    disabled={isArchiving || !selectedArchiveUser}
                    className={`inline-flex justify-center items-center py-2 px-4 
                      border border-transparent shadow-sm text-sm font-medium
                      rounded-md text-white
                      ${
                        isArchiving || !selectedArchiveUser
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-violet-500 hover:bg-violet-600"
                      } focus:outline-none focus:ring-2 focus:ring-offset-2 
                      focus:ring-violet-500`}
                  >
                    {isArchiving ? (
                      <>
                        <RefreshCcwIcon
                          className="animate-spin mr-2 h-5 w-5 text-white"
                          aria-hidden="true"
                        />
                        Archiving...
                      </>
                    ) : (
                      "Archive User"
                    )}
                  </button>
                )}

                {/* Archive success / error feedback */}
                {archiveSuccess !== null && (
                  <div className="mt-2 sm:mt-0 sm:ml-2 flex items-center">
                    {archiveSuccess ? (
                      <>
                        <CheckIcon className="h-6 w-6 text-green-500" />
                        <span className="ml-2 text-sm text-green-500">
                          {selectedArchiveUser?.id} successfully archived
                        </span>
                      </>
                    ) : (
                      <>
                        <XIcon className="h-6 w-6 text-violet-500" />
                        <span className="ml-2 text-sm text-violet-500">
                          {archiveError || "Failed to archive user"}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Reset archive form link */}
              {isArchived && (
                <button
                  type="button"
                  onClick={resetArchiveForm}
                  className="text-sm font-medium text-violet-600 underline hover:text-violet-800"
                >
                  Archive another user
                </button>
              )}
            </div>

            {/* ════════════════════════════════════════════════════════════
                RESTORE SECTION(NEW)
            ════════════════════════════════════════════════════════════ */}
            <div className="space-y-4">

              {/* Select User Combobox (archived + disabled users only) */}
              {renderCombobox(
                "Select User to Restore",
                selectedRestoreUser,
                setSelectedRestoreUser,
                restoreQuery,
                setRestoreQuery,
                filteredRestoreUsers,
                availableRestoreUsers,
                isLoadingAllUsers,
                isRestoring || isRestored,
                "No archived or disabled users available."
              )}

              {/* Selected restore user status badge */}
              {selectedRestoreUser && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">Current status:</span>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full
                      text-xs font-medium ${
                        selectedRestoreUser.user_status === "archived"
                          ? "bg-gray-100 text-gray-700"
                          : selectedRestoreUser.user_status === "disabled"
                          ? "bg-red-100 text-red-700"
                          : "bg-green-100 text-green-700"
                      }`}
                  >
                    {selectedRestoreUser.user_status ?? "enabled"}
                  </span>
                </div>
              )}

              {/* Restore success banner */}
              {isRestored && selectedRestoreUser && (
                <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3">
                  <p className="text-sm font-medium text-green-800">
                    ✅ User <strong>{selectedRestoreUser.id}</strong> has been
                    restored successfully.
                  </p>
                  <p className="mt-1 text-sm text-green-700">
                    The user is now active and visible on the UI. Their Cognito
                    account has been re-enabled.
                  </p>
                </div>
              )}

              {/* Restore action row */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                {/* Restore button */}
                {!isRestored && (
                  <button
                    type="button"
                    onClick={handleRestoreClick}
                    disabled={isRestoring || !selectedRestoreUser}
                    className={`inline-flex justify-center items-center py-2 px-4 
                      border border-transparent shadow-sm text-sm font-medium 
                      rounded-md text-white
                      ${
                        isRestoring || !selectedRestoreUser
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-violet-600 hover:bg-violet-700"
                      } focus:outline-none focus:ring-2 focus:ring-offset-2 
                      focus:ring-violet-500`}
                  >
                    {isRestoring ? (
                      <>
                        <RefreshCcwIcon
                          className="animate-spin mr-2 h-5 w-5 text-white"
                          aria-hidden="true"
                        />
                        Restoring...
                      </>
                    ) : (
                      "Restore User"
                    )}
                  </button>
                )}

                {/* Restore success / error feedback */}
                {restoreSuccess !== null && (
                  <div className="mt-2 sm:mt-0 sm:ml-2 flex items-center">
                    {restoreSuccess ? (
                      <>
                        <CheckIcon className="h-6 w-6 text-green-500" />
                        <span className="ml-2 text-sm text-green-500">
                          {selectedRestoreUser?.id} successfully restored
                        </span>
                      </>
                    ) : (
                      <>
                        <XIcon className="h-6 w-6 text-violet-500" />
                        <span className="ml-2 text-sm text-violet-500">
                          {restoreError || "Failed to restore user"}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Reset restore form link */}
              {isRestored && (
                <button
                  type="button"
                  onClick={resetRestoreForm}
                  className="text-sm font-medium text-violet-600 underline hover:text-violet-800"
                >
                  Restore another user
                </button>
              )}
            </div></div>
        </DisclosurePanel>
      </Disclosure>

      {/* ── Archive Confirmation Modal ──────────────────────────────────────── */}
      {showArchiveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center scrim px-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-center text-lg font-semibold text-gray-900">
              Archive User?
            </h3>
            <p className="mt-2 text-center text-sm text-gray-600">
              Are you sure you want to archive{" "}
              <strong className="text-gray-900">
                {selectedArchiveUser?.id}
              </strong>
              ?
              <br />
              <span className="text-gray-500text-xs mt-1 block">
                The user will be hidden from the UI and their Cognito account
                will be disabled. Their sessions and data will remain in the
                database.
              </span>
            </p>
            <div className="mt-6 flex space-x-3">
              <button
                onClick={() => setShowArchiveModal(false)}
                className="flex-1 rounded-md border border-gray-300 bg-whitepx-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50
                  focus:outline-none focus:ring-2 focus:ring-offset-2
                  focus:ring-violet-500"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmArchive}
                className="flex-1 rounded-md px-4 py-2 text-sm font-medium
                  text-white bg-violet-500 hover:bg-violet-600
                  focus:outline-none focus:ring-2 focus:ring-offset-2
                  focus:ring-violet-500 transition-colors"
              >
                Yes, Archive
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Restore Confirmation Modal ──────────────────────────────────────── */}
      {showRestoreModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center scrim px-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-center text-lg font-semibold text-gray-900">
              Restore User?
            </h3>
            <p className="mt-2 text-center text-sm text-gray-600">
              Are you sure you want to restore{" "}
              <strong className="text-gray-900">
                {selectedRestoreUser?.id}
              </strong>
              ?
              <br />
              <span className="text-gray-500 text-xs mt-1 block">
                The user will become visible on the UI again and their Cognito
                account will be re-enabled.
              </span>
            </p>
            <div className="mt-6 flex space-x-3">
              <button
                onClick={() => setShowRestoreModal(false)}
                className="flex-1 rounded-md border border-gray-300 bg-white
                  px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50
                  focus:outline-none focus:ring-2 focus:ring-offset-2
                  focus:ring-violet-500"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRestore}
                className="flex-1 rounded-md px-4 py-2 text-sm font-medium
                  text-white bg-violet-600 hover:bg-violet-700
                  focus:outline-none focus:ring-2 focus:ring-offset-2
                  focus:ring-violet-500 transition-colors"
              >
                Yes, Restore
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ArchiveUser;